import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { navigateAccountPortal } from "lib/staffFrontend";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import shortLogo from "assets/images/logos/ShortLogo.png";
import {
  MxHeaderAccountIcon,
  MxHeaderBackIcon,
  MxHeaderCartIcon,
  MxHeaderCatalogIcon,
  MxHeaderQrScanIcon,
} from "./MxHeaderIcons";
import { mx } from "../lib/theme";

function HeaderTap({ onClick, label, children }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      sx={{
        p: 0.35,
        minWidth: 40,
        minHeight: 40,
        display: "grid",
        placeItems: "center",
        borderRadius: "8px",
        lineHeight: 0,
        transition: "opacity 0.15s ease",
        "&:active": { opacity: 0.65 },
      }}
    >
      {children}
    </ButtonBase>
  );
}

HeaderTap.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function MxShell({
  children,
  title,
  subtitle,
  onBack,
  showLogo = false,
  rightAction,
  footer,
  bleed = false,
}) {
  const navigate = useNavigate();
  const { token, pluginRole } = useAuth();
  const { itemCount } = useCart();
  const { t } = useStorefrontCopy();

  const heroHeader = !!bleed;

  const defaultRight = (
    <Stack direction="row" spacing={0.15} alignItems="center">
      <HeaderTap label={t("common.catalog", "Productos")} onClick={() => navigate("/m/catalog")}>
        <MxHeaderCatalogIcon hero={heroHeader} />
      </HeaderTap>
      <HeaderTap
        label={t("registeredUnits.scanTitle", "Escanear")}
        onClick={() => navigate("/m/scan")}
      >
        <MxHeaderQrScanIcon hero={heroHeader} />
      </HeaderTap>
      {token ? (
        <>
          <HeaderTap
            label={t("common.account", "Cuenta")}
            onClick={() => navigateAccountPortal({ token, role: pluginRole, navigate })}
          >
            <MxHeaderAccountIcon hero={heroHeader} />
          </HeaderTap>
          <HeaderTap label={t("common.cart", "Carrito")} onClick={() => navigate("/m/cart")}>
            <Badge
              badgeContent={itemCount || 0}
              color="error"
              invisible={!itemCount}
              sx={{ "& .MuiBadge-badge": { fontWeight: 700, fontSize: 10, minWidth: 16, height: 16 } }}
            >
              <MxHeaderCartIcon hero={heroHeader} />
            </Badge>
          </HeaderTap>
        </>
      ) : (
        <ButtonBase
          onClick={() => navigate("/m/authentication/sign-in")}
          sx={{
            px: 0.5,
            py: 0.35,
            minHeight: 40,
            color: heroHeader ? "rgba(255,255,255,0.94)" : mx.ink,
            fontSize: 14,
            fontWeight: 650,
            letterSpacing: 0.02,
            borderRadius: "8px",
            border: heroHeader ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${mx.hairline}`,
          }}
        >
          {t("common.signIn", "Entrar")}
        </ButtonBase>
      )}
    </Stack>
  );

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: heroHeader ? mx.ink : mx.canvas, color: mx.ink }}>
      <Box
        sx={{
          position: bleed ? "absolute" : "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          bgcolor: heroHeader
            ? "linear-gradient(180deg, rgba(15,15,18,0.75) 0%, rgba(15,15,18,0.28) 65%, transparent 100%)"
            : "#FFFFFF",
          backdropFilter: heroHeader ? "none" : "blur(12px)",
          borderBottom: heroHeader ? "none" : `1px solid ${mx.hairline}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 2,
            pt: "max(10px, env(safe-area-inset-top))",
            pb: 1,
            minHeight: 52,
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ flex: "1 1 0", minWidth: 0, justifyContent: "flex-start" }}
          >
            {onBack ? (
              <ButtonBase
                onClick={onBack}
                aria-label={t("common.back", "Atrás")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  p: 0.35,
                  minHeight: 40,
                  color: mx.ink,
                  borderRadius: "8px",
                  "&:active": { opacity: 0.65 },
                }}
              >
                <MxHeaderBackIcon />
              </ButtonBase>
            ) : showLogo ? (
              <Box
                component="img"
                src={shortLogo}
                alt="VBS México"
                onClick={() => navigate("/m")}
                sx={{
                  height: 38,
                  width: "auto",
                  maxWidth: 132,
                  cursor: "pointer",
                  objectFit: "contain",
                  objectPosition: "left center",
                  display: "block",
                }}
              />
            ) : null}
          </Stack>

          {title && !bleed ? (
            <Typography
              component="div"
              sx={{
                flex: "0 1 auto",
                minWidth: 0,
                px: 0.5,
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: -0.3,
                lineHeight: 1.15,
                color: mx.ink,
                textAlign: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </Typography>
          ) : null}

          <Box
            sx={{
              flex: "1 1 0",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>{rightAction || defaultRight}</Box>
          </Box>
        </Box>

        {subtitle && title && !bleed ? (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography sx={{ fontSize: 13, color: mx.muted, lineHeight: 1.4, textAlign: "center" }}>
              {subtitle}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ px: bleed || showLogo ? 0 : 2, pt: bleed || showLogo ? 0 : 0.5, pb: footer ? 18 : 4, color: mx.ink }}>{children}</Box>

      {footer ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: "calc(70px + env(safe-area-inset-bottom))",
            zIndex: 1400,
            px: 2,
            py: 1.25,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(16px)",
            borderTop: `1px solid ${mx.hairline}`,
            boxShadow: "0 -10px 28px rgba(15,15,18,0.06)",
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}

MxShell.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onBack: PropTypes.func,
  showLogo: PropTypes.bool,
  rightAction: PropTypes.node,
  footer: PropTypes.node,
  bleed: PropTypes.bool,
};

MxShell.defaultProps = {
  title: "",
  subtitle: "",
  onBack: null,
  showLogo: false,
  rightAction: null,
  footer: null,
  bleed: false,
};
